import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TicketIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusCircleIcon,
  ChatBubbleLeftEllipsisIcon,
  CloudArrowDownIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchTicketingConfigs,
  updateTicketingConfig,
  testTicketingConnection,
  ticketingTestTicket,
  fetchTenantTicketingRoutes,
  fetchTicketingOptions,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { SEVERITY_OPTIONS, severityLabel } from '../data/severities';
import CwPermissionGuideDrawer from './CwPermissionGuideDrawer';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

// Fields are split into two groups, rendered with a divider:
//  - group 'connection' (REQUIRED): the creds the PSA needs to authenticate.
//  - group 'default' (OPTIONAL): MSP-wide routing defaults the Ticket Routing page
//    auto-applies to new tenants (Company is NOT a routing default — it's per-client,
//    kept here only so the test-ticket buttons can run without selecting a tenant).
// `optionType` routing-default fields render as PSA dropdowns after "Fetch from PSA".
const FIELD_DEFS = {
  autotask: [
    { key: 'baseUrl', label: 'Base URL', type: 'text', required: true, group: 'connection', placeholder: 'https://webservicesNN.autotask.net/atservicesrest' },
    { key: 'integrationCode', label: 'Integration Code', type: 'password', required: true, group: 'connection' },
    { key: 'userName', label: 'API User Name', type: 'text', required: true, group: 'connection' },
    { key: 'secret', label: 'Secret', type: 'password', required: true, group: 'connection' },
    { key: 'companyId', label: 'Company (test ticket only)', type: 'number', group: 'default', optionType: 'companies' },
    { key: 'queueId', label: 'Default Queue', type: 'number', group: 'default', optionType: 'boards' },
    { key: 'statusNew', label: 'Default New Status', type: 'number', group: 'default', optionType: 'statuses' },
    { key: 'statusComplete', label: 'Default Completed Status', type: 'number', group: 'default', optionType: 'statuses' },
    { key: 'priorityDefault', label: 'Default Priority', type: 'number', group: 'default', optionType: 'priorities' },
    // Advanced: which AutoTask ticket statuses the Notification-Dispatcher's close
    // poller treats as "resolved/complete" (multi-select; array of status ids).
    // Defaults to Complete (5) on the dispatcher side when left empty. ConnectWise
    // uses its native closedFlag, so this field is AutoTask-only.
    { key: 'closedStatusIds', label: 'Ticket-Closed Status IDs (advanced)', type: 'idlist', group: 'default', optionType: 'statuses' },
  ],
  connectwise: [
    { key: 'site', label: 'Site', type: 'text', required: true, group: 'connection', placeholder: 'https://api-na.myconnectwise.net' },
    { key: 'companyId', label: 'Company ID (login)', type: 'text', required: true, group: 'connection' },
    { key: 'publicKey', label: 'Public Key', type: 'password', required: true, group: 'connection' },
    { key: 'privateKey', label: 'Private Key', type: 'password', required: true, group: 'connection' },
    { key: 'clientId', label: 'Client ID', type: 'text', required: true, group: 'connection' },
    { key: 'ticketCompanyId', label: 'Company (test ticket only)', type: 'number', group: 'default', optionType: 'companies' },
    { key: 'boardId', label: 'Default Board', type: 'number', group: 'default', optionType: 'boards' },
    { key: 'statusId', label: 'Default New Status', type: 'number', group: 'default', optionType: 'statuses' },
    { key: 'statusComplete', label: 'Default Completed Status', type: 'number', group: 'default', optionType: 'statuses' },
    { key: 'priorityDefault', label: 'Default Priority', type: 'number', group: 'default', optionType: 'priorities' },
  ],
};

// PSA option dropdown (shows the current stored id as a fallback option).
function OptionSelect({ value, options, disabled, onChange, placeholder }) {
  const hasOptions = Array.isArray(options) && options.length > 0;
  const currentInList = hasOptions && options.some((o) => `${o.id}` === `${value || ''}`);
  return (
    <select
      value={`${value || ''}`}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="input-field w-full text-xs disabled:opacity-60"
    >
      <option value="">{placeholder || '— select —'}</option>
      {value && !currentInList && <option value={`${value}`}>{`current: ${value}`}</option>}
      {hasOptions && options.map((o) => (
        <option key={`${o.id}`} value={`${o.id}`}>{o.name ? `${o.name} (${o.id})` : `${o.id}`}</option>
      ))}
    </select>
  );
}

// Parse a comma-separated id list ("5, 12") into a de-duped array of positive
// numbers. Used by the `idlist` field type (AutoTask closedStatusIds).
function parseIdList(csv) {
  return [...new Set(
    `${csv ?? ''}`.split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0)
  )];
}

// Toggle an id in a CSV id-list string (used by the multi-select checkboxes).
function toggleIdCsv(csv, id) {
  const key = `${id}`;
  const cur = parseIdList(csv).map((n) => `${n}`);
  const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key];
  return next.join(',');
}

// Build the credential payload pieces from the in-form values, split by group so
// MSP-default changes can be saved WITHOUT re-entering write-only connection creds:
//  - bundle: full set (connection + defaults + priorityMap) — replaces the stored bundle
//  - defaultsPatch: default-group fields + priorityMap only — merged into the saved bundle
//  - connectionStarted/connectionComplete: state of the required connection fields
//  - defaultsStarted: any default field or priority was set
// priorityMap is a structured { severity: id } object (the 5 severity dropdowns).
function buildBundle(system, fields, priorityMap) {
  const defs = FIELD_DEFS[system] || [];
  const bundle = {};
  const defaultsPatch = {};
  let connectionStarted = false;
  let connectionComplete = true;
  let defaultsStarted = false;
  defs.forEach((d) => {
    const raw = (fields[d.key] ?? '').toString().trim();
    // idlist (always a 'default'-group field): store as an array of numbers so the
    // dispatcher reads it as a JSON array; empty = "not set" (dispatcher defaults it).
    if (d.type === 'idlist') {
      const ids = parseIdList(raw);
      if (ids.length) {
        bundle[d.key] = ids;
        defaultsStarted = true;
        defaultsPatch[d.key] = ids;
      }
      return;
    }
    if (raw) {
      const val = d.type === 'number' ? Number(raw) : raw;
      bundle[d.key] = val;
      if (d.group === 'connection') { connectionStarted = true; }
      else { defaultsStarted = true; defaultsPatch[d.key] = val; }
    } else if (d.required) {
      connectionComplete = false;
    }
  });
  const pm = {};
  Object.entries(priorityMap || {}).forEach(([sev, v]) => {
    const s = (v ?? '').toString().trim();
    if (s) pm[sev] = Number(s);
  });
  if (Object.keys(pm).length) {
    bundle.priorityMap = pm;
    defaultsPatch.priorityMap = pm;
    defaultsStarted = true;
  }
  return { bundle, defaultsPatch, connectionStarted, connectionComplete, defaultsStarted };
}

// Seed the editable form state for the DEFAULT-group fields + priority map from the
// server-provided non-secret `defaults` object (raw bundle keys). Connection fields
// are never seeded (write-only). Returns { fields, priorityMap } as strings.
function defaultFieldSeed(system, defaults) {
  const defs = FIELD_DEFS[system] || [];
  const fields = {};
  defs.filter((d) => d.group === 'default').forEach((d) => {
    const v = defaults ? defaults[d.key] : undefined;
    if (d.type === 'idlist') {
      // Backend surfaces closedStatusIds as an array; seed the form as CSV.
      if (Array.isArray(v) && v.length) fields[d.key] = v.map((x) => `${x}`.trim()).filter(Boolean).join(',');
      else if (v !== undefined && v !== null && `${v}` !== '') fields[d.key] = `${v}`;
      return;
    }
    if (v !== undefined && v !== null && `${v}` !== '') fields[d.key] = `${v}`;
  });
  const priorityMap = {};
  if (defaults && defaults.priorityMap) {
    Object.entries(defaults.priorityMap).forEach(([k, v]) => {
      if (v !== undefined && v !== null && `${v}` !== '') priorityMap[k] = `${v}`;
    });
  }
  return { fields, priorityMap };
}

export default function TicketingConfigManager() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const isMspAdmin = !!user?.isMspAdmin;
  const adminMspCodes = useMemo(
    () => (user?.adminMspCodes || []).map((c) => `${c}`.toUpperCase()),
    [user]
  );
  // A row is editable when the user is a global admin or administers that MSP.
  const canEdit = useCallback(
    (mspCode) => isGlobalAdmin || adminMspCodes.includes(`${mspCode || ''}`.toUpperCase()),
    [isGlobalAdmin, adminMspCodes]
  );
  // Page is reachable by global + MSP admins; anyone else gets a read-only view.
  const anyAdmin = isGlobalAdmin || isMspAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-MSP local edit state: { system, enabled, fields:{}, priorityMap:{} }.
  const [edits, setEdits] = useState({});
  // PSA option lists per MSP for the routing-default dropdowns.
  const [options, setOptions] = useState({});        // code -> { companies, boards, statuses, priorities }
  const [fetchingOptsCode, setFetchingOptsCode] = useState(null);
  const [optsMsg, setOptsMsg] = useState({});         // code -> { ok, message }
  const [expandedCode, setExpandedCode] = useState(null);
  const [savingCode, setSavingCode] = useState(null);
  const [testingCode, setTestingCode] = useState(null);
  const [rowError, setRowError] = useState({});
  const [testResult, setTestResult] = useState({}); // code -> { ok, message }
  // Test-ticket lifecycle (per-MSP, session-only). testTickets holds the ticket
  // created this session so Update/Close know what to act on; ticketActionCode is
  // the in-flight `${code}:${action}` marker; ticketResult is the inline message.
  const [testTickets, setTestTickets] = useState({}); // code -> { ticketId, ticketNumber, system }
  const [ticketActionCode, setTicketActionCode] = useState(null); // `${code}:${action}`
  const [ticketResult, setTicketResult] = useState({}); // code -> { ok, message }
  // Per-tenant routing rows (for the optional "Test as tenant" picker) + the picked
  // tenant per MSP — when set, the test ticket exercises that tenant's routing IDs.
  const [tenantRoutes, setTenantRoutes] = useState([]);
  const [testTenant, setTestTenant] = useState({}); // code -> tenantId

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTicketingConfigs();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const seed = {};
      list.forEach((r) => {
        // Connection creds are write-only (never seeded). Routing DEFAULTS are not
        // secret, so seed them so the form shows the real current values.
        const { fields, priorityMap } = defaultFieldSeed(r.ticketingSystem, r.defaults);
        seed[r.mspCode] = {
          system: r.ticketingSystem || 'disabled',
          enabled: !!r.enabled,
          fields,
          priorityMap,
          ticketUrlTemplate: r.ticketUrlTemplate || '',
        };
      });
      setEdits(seed);
      // Load configured per-tenant routes (best-effort) for the test-as-tenant picker.
      try {
        const routes = await fetchTenantTicketingRoutes();
        setTenantRoutes(Array.isArray(routes) ? routes.filter((t) => t.configured) : []);
      } catch { setTenantRoutes([]); }
    } catch (err) {
      setError(err.message || 'Failed to load ticketing config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setEdit = (code, patch) => {
    setEdits((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  };
  const setField = (code, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [code]: { ...prev[code], fields: { ...prev[code].fields, [key]: value } },
    }));
  };

  // Dirty if system/enabled changed, any CONNECTION cred was typed, or any DEFAULT
  // field / priority differs from the server's seeded baseline (r.defaults).
  const isDirty = useCallback((r) => {
    const e = edits[r.mspCode];
    if (!e) return false;
    if (e.system !== (r.ticketingSystem || 'disabled')) return true;
    if (e.enabled !== !!r.enabled) return true;
    if ((e.ticketUrlTemplate ?? '') !== (r.ticketUrlTemplate ?? '')) return true;
    const defs = FIELD_DEFS[e.system] || [];
    // Connection creds (re-)entered → dirty.
    if (defs.filter((d) => d.group === 'connection').some((d) => (e.fields[d.key] ?? '').toString().trim())) return true;
    // Default fields changed vs baseline.
    const baseline = defaultFieldSeed(e.system, r.defaults);
    if (defs.filter((d) => d.group === 'default').some((d) => `${e.fields[d.key] ?? ''}` !== `${baseline.fields[d.key] ?? ''}`)) return true;
    // Priority map changed vs baseline.
    return SEVERITY_OPTIONS.some((s) => `${(e.priorityMap || {})[s] ?? ''}` !== `${(baseline.priorityMap || {})[s] ?? ''}`);
  }, [edits]);

  const setPriority = (code, sev, value) =>
    setEdits((prev) => ({ ...prev, [code]: { ...prev[code], priorityMap: { ...(prev[code].priorityMap || {}), [sev]: value } } }));

  // Fetch PSA option lists for the MSP-default dropdowns (uses the SAVED bundle, so
  // save the connection creds first). AutoTask statuses are global; CW needs a board.
  const handleFetchOptions = async (r) => {
    const e = edits[r.mspCode];
    if (!e || e.system === 'disabled') return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setOptsMsg((prev) => ({ ...prev, [r.mspCode]: null }));
    if (!r.credentialsConfigured) {
      setOptsMsg((prev) => ({ ...prev, [r.mspCode]: { ok: false, message: 'Save the connection credentials first, then fetch.' } }));
      return;
    }
    const sys = e.system;
    const boardField = sys === 'connectwise' ? 'boardId' : 'queueId';
    const boardId = (e.fields[boardField] ?? '').toString().trim();
    const wantStatuses = sys === 'autotask' || !!boardId;
    setFetchingOptsCode(r.mspCode);
    try {
      const [companies, boards, priorities, statuses] = await Promise.all([
        fetchTicketingOptions(r.mspCode, 'companies'),
        fetchTicketingOptions(r.mspCode, 'boards'),
        fetchTicketingOptions(r.mspCode, 'priorities'),
        wantStatuses ? fetchTicketingOptions(r.mspCode, 'statuses', sys === 'autotask' ? undefined : boardId) : Promise.resolve({ options: [] }),
      ]);
      const opt = {
        companies: companies.options || [],
        boards: boards.options || [],
        priorities: priorities.options || [],
        statuses: statuses.options || [],
      };
      setOptions((prev) => ({ ...prev, [r.mspCode]: opt }));
      const queueWord = sys === 'autotask' ? 'queues' : 'boards';
      const statusNote = !wantStatuses ? ' (pick a board, then re-fetch for statuses)' : '';
      setOptsMsg((prev) => ({ ...prev, [r.mspCode]: { ok: true, message: `Loaded ${opt.companies.length} companies, ${opt.boards.length} ${queueWord}, ${opt.statuses.length} statuses, ${opt.priorities.length} priorities.${statusNote}` } }));
    } catch (err) {
      setOptsMsg((prev) => ({ ...prev, [r.mspCode]: { ok: false, message: err.message || 'Failed to fetch options from PSA' } }));
    } finally {
      setFetchingOptsCode(null);
    }
  };

  const handleSave = async (r) => {
    const e = edits[r.mspCode];
    if (!e) return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));

    const payload = { ticketingSystem: e.system, enabled: e.enabled, ticketUrlTemplate: e.ticketUrlTemplate ?? '' };
    if (e.system !== 'disabled') {
      const { bundle, defaultsPatch, connectionStarted, connectionComplete, defaultsStarted } = buildBundle(e.system, e.fields, e.priorityMap);
      if (connectionStarted) {
        // Replacing the creds → must enter all required connection fields.
        if (!connectionComplete) {
          setRowError((prev) => ({ ...prev, [r.mspCode]: 'Fill all required credential fields, or clear them to keep the saved credentials.' }));
          return;
        }
        payload.bundle = bundle; // full replace (connection + defaults)
      } else if (defaultsStarted) {
        // Changing only MSP defaults → merge into the saved bundle (no creds needed).
        if (!r.credentialsConfigured) {
          setRowError((prev) => ({ ...prev, [r.mspCode]: 'Save the connection credentials first, then set the MSP defaults.' }));
          return;
        }
        payload.bundlePatch = defaultsPatch;
      }
    }

    setSavingCode(r.mspCode);
    try {
      const updated = await updateTicketingConfig(r.mspCode, payload);
      setRows((prev) => prev.map((item) => (item.mspCode === r.mspCode ? { ...item, ...updated } : item)));
      // Re-seed from the saved state: connection creds cleared (write-only), routing
      // defaults re-populated from the server's read-back so the form shows real values.
      const { fields, priorityMap } = defaultFieldSeed(updated.ticketingSystem || e.system, updated.defaults);
      setEdit(r.mspCode, {
        system: updated.ticketingSystem || 'disabled',
        enabled: !!updated.enabled,
        fields,
        priorityMap,
        ticketUrlTemplate: updated.ticketUrlTemplate ?? '',
      });
      setTestResult((prev) => ({ ...prev, [r.mspCode]: null }));
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: err.message || 'Failed to save' }));
    } finally {
      setSavingCode(null);
    }
  };

  const handleTest = async (r) => {
    const e = edits[r.mspCode];
    if (!e || e.system === 'disabled') return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setTestResult((prev) => ({ ...prev, [r.mspCode]: null }));

    const { bundle, connectionStarted, connectionComplete } = buildBundle(e.system, e.fields, e.priorityMap);
    // Three cases:
    //  - typed a complete connection bundle → test that bundle
    //  - typed no connection creds but they're already saved → test the stored bundle
    //  - typed a partial connection bundle, or nothing saved → can't test
    let payload;
    if (connectionStarted) {
      if (!connectionComplete) {
        setRowError((prev) => ({ ...prev, [r.mspCode]: 'Fill all required credential fields, or clear them to test the saved credentials.' }));
        return;
      }
      payload = { ticketingSystem: e.system, bundle };
    } else if (r.credentialsConfigured) {
      payload = { ticketingSystem: e.system, useSaved: true };
    } else {
      setRowError((prev) => ({ ...prev, [r.mspCode]: 'Enter all required credential fields to test the connection.' }));
      return;
    }

    setTestingCode(r.mspCode);
    try {
      const res = await testTicketingConnection(r.mspCode, payload);
      setTestResult((prev) => ({ ...prev, [r.mspCode]: { ok: !!res.ok, message: res.message || (res.ok ? 'OK' : 'Failed') } }));
    } catch (err) {
      setTestResult((prev) => ({ ...prev, [r.mspCode]: { ok: false, message: err.message || 'Connection test failed' } }));
    } finally {
      setTestingCode(null);
    }
  };

  // Resolve the credential payload for a test-ticket action (same logic as
  // handleTest): a fully-typed bundle, else the saved Key Vault bundle, else error.
  const buildTicketPayload = (r, e) => {
    const { bundle, connectionStarted, connectionComplete } = buildBundle(e.system, e.fields, e.priorityMap);
    if (connectionStarted) {
      if (!connectionComplete) return { error: 'Fill all required credential fields, or clear them to use the saved credentials.' };
      return { payload: { ticketingSystem: e.system, bundle } };
    }
    if (r.credentialsConfigured) return { payload: { ticketingSystem: e.system, useSaved: true } };
    return { error: 'Enter all required credential fields, or save credentials first.' };
  };

  const handleTicketAction = async (r, action) => {
    const e = edits[r.mspCode];
    if (!e || e.system === 'disabled') return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setTicketResult((prev) => ({ ...prev, [r.mspCode]: null }));

    const existing = testTickets[r.mspCode];
    if ((action === 'update' || action === 'close') && !existing?.ticketId) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: 'Create a test ticket first.' }));
      return;
    }

    const { payload, error } = buildTicketPayload(r, e);
    if (error) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: error }));
      return;
    }
    payload.action = action;
    if (action !== 'create') payload.ticketId = existing.ticketId;
    // Optional: exercise a specific tenant's routing row (authoritative path).
    const pickedTenant = testTenant[r.mspCode];
    if (pickedTenant) payload.tenantId = pickedTenant;

    setTicketActionCode(`${r.mspCode}:${action}`);
    try {
      const res = await ticketingTestTicket(r.mspCode, payload);
      setTicketResult((prev) => ({ ...prev, [r.mspCode]: { ok: !!res.ok, message: res.message || (res.ok ? 'OK' : 'Failed') } }));
      if (res.ok && action === 'create' && res.ticketId) {
        setTestTickets((prev) => ({ ...prev, [r.mspCode]: { ticketId: res.ticketId, ticketNumber: res.ticketNumber || res.ticketId, system: e.system } }));
      }
      if (res.ok && action === 'close') {
        setTestTickets((prev) => { const n = { ...prev }; delete n[r.mspCode]; return n; });
      }
    } catch (err) {
      setTicketResult((prev) => ({ ...prev, [r.mspCode]: { ok: false, message: err.message || `Failed to ${action} test ticket` } }));
    } finally {
      setTicketActionCode(null);
    }
  };

  const enabledCount = useMemo(() => rows.filter((r) => !!r.enabled).length, [rows]);
  const [showCwGuide, setShowCwGuide] = useState(false);

  const systemLabel = (s) => (s === 'autotask' ? 'AutoTask' : s === 'connectwise' ? 'ConnectWise' : 'Disabled');

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <TicketIcon className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-100">MSP Ticketing Integration</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-MSP PSA ticketing configuration. When an alert fires, the dispatcher opens a ticket in the
          MSP's system (Kaseya AutoTask or ConnectWise Manage). Credentials are stored in Key Vault and are
          write-only — saved secret values are never displayed here. Toggling ticketing on or off is
          independent of the Teams channel and email routing.
        </p>
        <button
          onClick={() => setShowCwGuide(true)}
          className="mt-2 text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1.5"
        >
          <BookOpenIcon className="h-4 w-4" />
          ConnectWise Manage Permission Guide
        </button>
      </div>

      {!anyAdmin && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to change ticketing config.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      <div className="flex items-center justify-end mb-3">
        <span className="text-xs text-gray-500">
          {rows.length} MSP{rows.length === 1 ? '' : 's'} · {enabledCount} with ticketing enabled
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-gray-800">
                {['', 'MSP', 'System', 'Credentials', 'Enabled', 'Last Updated', ''].map((label, i) => (
                  <th key={i} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
                      <span className="text-gray-500 text-sm">Loading ticketing config...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <TicketIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No MSPs configured</p>
                    <p className="text-gray-600 text-sm mt-1">Set the MSP_CATALOG app setting to populate this list.</p>
                  </td>
                </tr>
              )}

              {!loading && rows.map((r) => {
                const e = edits[r.mspCode] || { system: 'disabled', enabled: false, fields: {}, priorityMap: '' };
                const editable = canEdit(r.mspCode);
                const dirty = isDirty(r);
                const saving = savingCode === r.mspCode;
                const testing = testingCode === r.mspCode;
                const expanded = expandedCode === r.mspCode;
                const tr = testResult[r.mspCode];
                const tt = testTickets[r.mspCode];          // session test ticket for this MSP
                const ttRes = ticketResult[r.mspCode];      // create/update/close result message
                const acting = (a) => ticketActionCode === `${r.mspCode}:${a}`;
                const anyActing = ticketActionCode && ticketActionCode.startsWith(`${r.mspCode}:`);
                const defs = FIELD_DEFS[e.system] || [];
                return (
                  <React.Fragment key={r.mspCode}>
                    <tr className="hover:bg-gray-800/50 transition-colors align-top">
                      <td className="px-2 py-3 w-8">
                        {e.system !== 'disabled' && (
                          <button
                            onClick={() => setExpandedCode(expanded ? null : r.mspCode)}
                            className="p-0.5 text-gray-500 hover:text-blue-400"
                            title={expanded ? 'Hide credentials' : 'Configure credentials'}
                          >
                            {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-gray-200">{r.mspCode}</div>
                        {r.displayName && <div className="text-xs text-gray-500">{r.displayName}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={e.system}
                          disabled={!editable}
                          onChange={(ev) => {
                            const sys = ev.target.value;
                            setEdit(r.mspCode, { system: sys, enabled: sys === 'disabled' ? false : e.enabled });
                            if (sys === 'disabled') setExpandedCode((c) => (c === r.mspCode ? null : c));
                            else setExpandedCode(r.mspCode);
                          }}
                          className="input-field text-xs disabled:opacity-60"
                        >
                          <option value="disabled">Disabled</option>
                          <option value="autotask">AutoTask</option>
                          <option value="connectwise">ConnectWise</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {r.credentialsConfigured
                          ? <span className="inline-flex items-center gap-1 text-green-400"><CheckCircleIcon className="h-4 w-4" /> Configured</span>
                          : <span className="text-gray-600">Not set</span>}
                      </td>
                      <td className="px-4 py-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={e.enabled}
                            disabled={!editable || e.system === 'disabled'}
                            onChange={(ev) => setEdit(r.mspCode, { enabled: ev.target.checked })}
                            className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 disabled:opacity-40"
                          />
                          <span className={`text-xs ${e.enabled ? 'text-green-400' : 'text-gray-500'}`}>
                            {e.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(r.updatedUtc)}
                        {r.updatedBy && <div className="text-gray-600 truncate max-w-[180px]" title={r.updatedBy}>{r.updatedBy}</div>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editable && (
                          <button
                            onClick={() => handleSave(r)}
                            disabled={!dirty || saving}
                            className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                          >
                            {saving ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckIcon className="h-3.5 w-3.5" />}
                            Save
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Credential form (write-only) */}
                    {expanded && e.system !== 'disabled' && (
                      <tr className="bg-gray-900/40">
                        <td></td>
                        <td colSpan={6} className="px-4 py-4">
                          <div className="text-xs text-gray-500 mb-3">
                            {systemLabel(e.system)} credentials — leave blank to keep the saved values. Entering
                            credentials replaces the stored bundle on save.
                          </div>

                          {/* PSA web-UI ticket URL template (NOT a secret) */}
                          <div className="mb-4 max-w-4xl">
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                              Ticket URL template (web-UI link)
                            </label>
                            <input
                              type="text"
                              value={e.ticketUrlTemplate ?? ''}
                              disabled={!editable}
                              onChange={(ev) => setEdit(r.mspCode, { ticketUrlTemplate: ev.target.value })}
                              placeholder={e.system === 'connectwise'
                                ? 'https://<your-cw-host>/...&recid={ticketId}'
                                : 'https://ww14.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId={ticketId}'}
                              className="input-field w-full text-xs font-mono disabled:opacity-60"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                              Builds the clickable ticket link shown on alerts. Put <span className="font-mono text-gray-400">{'{ticketId}'}</span> where the ticket id goes;
                              if you omit it, the id is appended to the end (so a value ending in <span className="font-mono text-gray-400">…?ticketId=</span> also works). Leave blank to auto-derive from the PSA host.
                            </p>
                          </div>

                          {/* Connection (required) */}
                          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Connection</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
                            {defs.filter((d) => d.group === 'connection').map((d) => (
                              <div key={d.key}>
                                <label className="block text-[11px] text-gray-500 mb-1">
                                  {d.label}{d.required && <span className="text-red-400"> *</span>}
                                </label>
                                <input
                                  type={d.type === 'number' ? 'number' : d.type}
                                  autoComplete={d.type === 'password' ? 'new-password' : 'off'}
                                  value={e.fields[d.key] ?? ''}
                                  disabled={!editable}
                                  onChange={(ev) => setField(r.mspCode, d.key, ev.target.value)}
                                  placeholder={r.credentialsConfigured ? '•••••• (saved)' : (d.placeholder || '')}
                                  className="input-field w-full text-xs disabled:opacity-60"
                                />
                              </div>
                            ))}
                          </div>

                          {/* Divider: required connection above, optional MSP defaults below */}
                          <hr className="my-4 border-gray-700/60" />

                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">MSP Defaults (optional)</span>
                            {editable && (
                              <button
                                onClick={() => handleFetchOptions(r)}
                                disabled={fetchingOptsCode === r.mspCode}
                                title="Fetch real Queue / Status / Priority values from the PSA (save connection creds first)"
                                className="btn-secondary px-2.5 py-1 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                              >
                                {fetchingOptsCode === r.mspCode ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CloudArrowDownIcon className="h-3.5 w-3.5" />}
                                Fetch from PSA
                              </button>
                            )}
                            {optsMsg[r.mspCode] && (
                              <span className={`text-xs inline-flex items-center gap-1 ${optsMsg[r.mspCode].ok ? 'text-green-400' : 'text-red-400'}`}>
                                {optsMsg[r.mspCode].ok ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}{optsMsg[r.mspCode].message}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 mb-2">
                            Auto-applied to new clients on the Ticket Routing page. Company is per-client and is not defaulted.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
                            {defs.filter((d) => d.group === 'default').map((d) => {
                              const opt = options[r.mspCode] || {};
                              const list = d.optionType ? opt[d.optionType] : null;
                              const asPicker = d.optionType && Array.isArray(list) && list.length > 0;
                              // idlist (AutoTask closedStatusIds): multi-select of statuses
                              // when fetched, else a comma-separated fallback text input.
                              if (d.type === 'idlist') {
                                const selSet = new Set(parseIdList(e.fields[d.key] ?? '').map((n) => `${n}`));
                                return (
                                  <div key={d.key} className="sm:col-span-2 lg:col-span-3">
                                    <label className="block text-[11px] text-gray-500 mb-1">
                                      {d.label}
                                      <span className="ml-1 normal-case text-gray-600">— statuses the dispatcher counts as complete; defaults to Complete (5) when empty.</span>
                                    </label>
                                    {asPicker ? (
                                      <div className="flex flex-wrap gap-x-4 gap-y-1 max-h-32 overflow-y-auto rounded border border-gray-700/60 bg-gray-900/40 p-2">
                                        {list.map((o) => (
                                          <label key={`${o.id}`} className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                                            <input
                                              type="checkbox"
                                              checked={selSet.has(`${o.id}`)}
                                              disabled={!editable}
                                              onChange={() => setField(r.mspCode, d.key, toggleIdCsv(e.fields[d.key] ?? '', o.id))}
                                              className="h-3.5 w-3.5 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 disabled:opacity-40"
                                            />
                                            {o.name ? `${o.name} (${o.id})` : `${o.id}`}
                                          </label>
                                        ))}
                                      </div>
                                    ) : (
                                      <input
                                        type="text"
                                        value={e.fields[d.key] ?? ''}
                                        disabled={!editable}
                                        onChange={(ev) => setField(r.mspCode, d.key, ev.target.value)}
                                        placeholder="e.g. 5, 12 — or Fetch from PSA to pick statuses"
                                        className="input-field w-full text-xs disabled:opacity-60"
                                      />
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <div key={d.key}>
                                  <label className="block text-[11px] text-gray-500 mb-1">{d.label}</label>
                                  {asPicker ? (
                                    <OptionSelect
                                      value={e.fields[d.key] ?? ''}
                                      options={list}
                                      disabled={!editable}
                                      onChange={(v) => setField(r.mspCode, d.key, v)}
                                      placeholder="— not set —"
                                    />
                                  ) : (
                                    <input
                                      type={d.type === 'number' ? 'number' : d.type}
                                      value={e.fields[d.key] ?? ''}
                                      disabled={!editable}
                                      onChange={(ev) => setField(r.mspCode, d.key, ev.target.value)}
                                      placeholder={r.credentialsConfigured ? 'not set — Fetch from PSA to pick' : 'Fetch from PSA to pick'}
                                      className="input-field w-full text-xs disabled:opacity-60"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Per-severity priority map (alert severity → PSA priority) */}
                          <div className="mt-3">
                            <label className="block text-[11px] text-gray-500 mb-1">Priority Map — alert severity → PSA priority (optional)</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-4xl">
                              {SEVERITY_OPTIONS.map((sev) => {
                                const prios = (options[r.mspCode] || {}).priorities;
                                const asPicker = Array.isArray(prios) && prios.length > 0;
                                return (
                                  <div key={sev}>
                                    <label className="block text-[11px] text-gray-500 mb-1">{severityLabel(sev)}</label>
                                    {asPicker ? (
                                      <OptionSelect
                                        value={(e.priorityMap || {})[sev] || ''}
                                        options={prios}
                                        disabled={!editable}
                                        onChange={(v) => setPriority(r.mspCode, sev, v)}
                                        placeholder="— none —"
                                      />
                                    ) : (
                                      <input
                                        type="number"
                                        value={(e.priorityMap || {})[sev] || ''}
                                        disabled={!editable}
                                        onChange={(ev) => setPriority(r.mspCode, sev, ev.target.value)}
                                        placeholder="id"
                                        className="input-field w-full text-xs disabled:opacity-60"
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 mt-3 flex-wrap">
                            {editable && (
                              <button
                                onClick={() => handleTest(r)}
                                disabled={testing}
                                className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                              >
                                {testing ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
                                Test connection
                              </button>
                            )}
                            {tr && (
                              <span className={`text-xs inline-flex items-center gap-1 ${tr.ok ? 'text-green-400' : 'text-red-400'}`}>
                                {tr.ok ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
                                {tr.message}
                              </span>
                            )}
                          </div>

                          {/* Test-ticket lifecycle — verify the write path (create → comment → close) */}
                          {editable && (
                            <div className="mt-4 pt-3 border-t border-gray-800/60">
                              <div className="text-[11px] text-gray-500 mb-2">
                                Test ticket — opens a real ticket in this PSA to verify create / comment / close, then close it out.
                              </div>
                              {(() => {
                                const mspTenants = tenantRoutes.filter((t) => `${t.mspCode}`.toUpperCase() === `${r.mspCode}`.toUpperCase());
                                if (mspTenants.length === 0) return null;
                                return (
                                  <div className="mb-2 flex items-center gap-2 flex-wrap">
                                    <label className="text-[11px] text-gray-500">Test as tenant (uses its routing IDs):</label>
                                    <select
                                      value={testTenant[r.mspCode] || ''}
                                      onChange={(ev) => setTestTenant((prev) => ({ ...prev, [r.mspCode]: ev.target.value }))}
                                      className="input-field text-xs"
                                    >
                                      <option value="">MSP credential bundle (best-effort)</option>
                                      {mspTenants.map((t) => (
                                        <option key={t.tenantId} value={t.tenantId}>{t.friendlyName || t.tenantId}</option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              })()}
                              <div className="flex items-center gap-3 flex-wrap">
                                <button
                                  onClick={() => handleTicketAction(r, 'create')}
                                  disabled={anyActing}
                                  className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                                  title="Open a new test ticket in the PSA"
                                >
                                  {acting('create') ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlusCircleIcon className="h-3.5 w-3.5" />}
                                  Create test ticket
                                </button>
                                <button
                                  onClick={() => handleTicketAction(r, 'update')}
                                  disabled={anyActing || !tt?.ticketId}
                                  className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                                  title={tt?.ticketId ? 'Add the comment "test ticket update"' : 'Create a test ticket first'}
                                >
                                  {acting('update') ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" />}
                                  Update
                                </button>
                                <button
                                  onClick={() => handleTicketAction(r, 'close')}
                                  disabled={anyActing || !tt?.ticketId}
                                  className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                                  title={tt?.ticketId ? 'Close the test ticket' : 'Create a test ticket first'}
                                >
                                  {acting('close') ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <XCircleIcon className="h-3.5 w-3.5" />}
                                  Close
                                </button>
                                {tt?.ticketId && (
                                  <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                                    <TicketIcon className="h-3.5 w-3.5 text-blue-400" />
                                    Ticket #{tt.ticketNumber}
                                  </span>
                                )}
                                {ttRes && (
                                  <span className={`text-xs inline-flex items-center gap-1 ${ttRes.ok ? 'text-green-400' : 'text-red-400'}`}>
                                    {ttRes.ok ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
                                    {ttRes.message}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {rowError[r.mspCode] && <p className="mt-2 text-xs text-red-400">{rowError[r.mspCode]}</p>}
                        </td>
                      </tr>
                    )}

                    {/* Inline row error when collapsed */}
                    {!expanded && rowError[r.mspCode] && (
                      <tr className="bg-gray-900/40">
                        <td></td>
                        <td colSpan={6} className="px-4 py-2 text-xs text-red-400">{rowError[r.mspCode]}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCwGuide && <CwPermissionGuideDrawer onClose={() => setShowCwGuide(false)} />}
    </div>
  );
}
